import { DateTime, type DurationLikeObject, type Zone } from 'luxon';
import { RRule, RRuleSet } from 'rrule';
import { validate as validateUuid } from 'uuid';
import { TIME_ZONE_UTC } from '@/constants';
import { datePartInZone } from '@/utils/dates';
import type { DecoratedEvent } from '../types';
import type { DateTimeRange } from './range.lib';

type InstanceTiming = Pick<DecoratedEvent, 'isAllDay' | 'startDate'>;

type RecurringEvent = InstanceTiming &
  Pick<DecoratedEvent, 'timeZone' | 'rrule'>;

/**
 * How far ahead to expand a rule with no UNTIL/COUNT. Every expander shares it —
 * the printable/planner grid, the EventList collection, and the instance
 * resolver below — so they all agree on where an open-ended series stops.
 *
 * Typed as luxon's `DurationLikeObject` (what `DateTime.plus` takes) so any unit
 * it supports is available here — `{ years: 2 }`, `{ months: 6, days: 15 }` — and
 * a typo like `{ month: 18 }` is a compile error rather than a silent no-op.
 */
export const RECURRENCE_WINDOW: DurationLikeObject = { months: 18 };

/**
 * What the fallback window below is measured from: the span being rendered when
 * the caller has one, otherwise now. `Partial<DateTimeRange>` because a
 * configured range can be half-set or absent, while a resolved view range
 * passes straight through.
 *
 * Anchoring on the range rather than on today is what lets a view of a span
 * other than the current one work. A printable configured for a range starting
 * beyond now + RECURRENCE_WINDOW has to expand occurrences there; measuring
 * from today would stop the window short of them and empty the view — the same
 * failure as measuring from `dtstart`, at the other end.
 *
 * The range *end* rather than its start, because the window has to reach the
 * far edge of what is being rendered: a range longer than RECURRENCE_WINDOW
 * would otherwise be truncated partway through. For any range shorter than the
 * window — every template period in practice — the two are equivalent, since
 * the window covers the whole range either way.
 */
export const getRecurrenceWindowAnchor = (
  now: DateTime,
  range?: Partial<DateTimeRange> | null
): DateTime => range?.endDate ?? range?.startDate ?? now;

/**
 * The fallback `UNTIL` for a rule that carries neither UNTIL nor COUNT.
 *
 * Measured from the later of the series start and the window anchor, never
 * from `dtstart` alone: a series that began more than one window ago would
 * otherwise end before the span being rendered and expand to zero occurrences,
 * so it would render nowhere. That is an ordinary case, not an edge one — ICS
 * and Google feeds store the rule unexpanded and the server keeps such a
 * series in range by stamping it with a 2037 `lastEndDate`, so a weekly event
 * created years ago still arrives here carrying its original start (AH-2430).
 *
 * Both fields have to be in the same space: rrule's fake-UTC anchor (see
 * `toRRuleAnchor`) for both, or real instants for both. Mixing them skews the
 * comparison by the recurrence zone's offset.
 */
export const getFallbackUntil = ({
  dtstart,
  windowAnchor,
}: {
  dtstart: DateTime;
  windowAnchor: DateTime;
}): DateTime => DateTime.max(dtstart, windowAnchor).plus(RECURRENCE_WINDOW);

/**
 * The single instance of an event that the edit modal is scoped to. Kept
 * separate from the event itself: `useUpdateEvent` derives the saved start/end
 * from `startDate`/`endDate`, so folding an instance into the event object
 * would move the whole series on the next save.
 */
export type EventInstance = {
  /** Display start, for labels and the past check. */
  start: DateTime;
  end: DateTime;
  /** Canonical instant for matching `EventMessage.instance`; null for single events. */
  key: DateTime | null;
  /** Wire value for Create/UpdateEventMessageInput.instance; null for single events. */
  isoStart: string | null;
  /**
   * All-day-ness of the *saved* event, which decides how the server serialized
   * the stored instance — see `toInstanceMillis`. Carried here so every consumer
   * decodes with the same value rather than reading it off an edit buffer.
   */
  isAllDay: boolean;
};

/**
 * The viewer-independent identity of one instance.
 *
 * All-day events normalize to UTC midnight of their calendar date — their
 * `startDate` is built at midnight in the viewer's display zone, so the raw
 * instant differs per viewer. Timed events use their absolute instant.
 *
 * This is the same value `metadata.hidden` is keyed by (see eventVisibility.lib)
 * and the same instant the server compares against (eventMessage.normalizeInstance),
 * so hidden instances and scheduled messages stay in agreement.
 */
export const getInstanceStart = (event: InstanceTiming): DateTime =>
  event.isAllDay
    ? datePartInZone(event.startDate, TIME_ZONE_UTC)
    : event.startDate;

/**
 * The `?event=` param value: `{uuid}` or `{uuid}:{epochMillis}`. Epoch millis
 * rather than an ISO instant because ISO carries its own colons.
 */
export const serializeEventKey = (
  eventId: string,
  instanceStart?: DateTime | null
): string =>
  instanceStart ? `${eventId}:${instanceStart.toMillis()}` : eventId;

/**
 * Inverse of `serializeEventKey`. A malformed instance segment degrades to the
 * bare event rather than a dead link.
 */
export const parseEventKey = (
  raw: string | null | undefined
): { eventId?: string; instanceStart?: DateTime } => {
  if (!raw) {
    return {};
  }

  const separator = raw.indexOf(':');
  const eventId = separator === -1 ? raw : raw.slice(0, separator);

  if (!validateUuid(eventId)) {
    return {};
  }

  if (separator === -1) {
    return { eventId };
  }

  const rest = raw.slice(separator + 1);

  // Digits only — Number('') is 0, which would silently resolve to the epoch
  // instead of being rejected.
  if (!/^\d+$/.test(rest)) {
    return { eventId };
  }

  try {
    // UTC, not local: an all-day instance is UTC midnight, and rehydrating it
    // in a negative-offset zone would render the previous calendar day.
    const instanceStart = DateTime.fromMillis(Number(rest), {
      zone: TIME_ZONE_UTC,
    });

    // Both guards are load-bearing: a millis luxon can't represent throws under
    // `Settings.throwOnInvalid` (the app sets it in src/index.tsx) but returns
    // an invalid DateTime anywhere that doesn't — tests included.
    return instanceStart.isValid ? { eventId, instanceStart } : { eventId };
  } catch {
    return { eventId };
  }
};

/**
 * Turns an instance key back into the value to show the user and compare
 * against now. An all-day key is UTC midnight, which is the wrong *instant* to
 * reason about locally — west of UTC it lands before the day has begun, so a
 * naive `key.toUTC() < now` marks tomorrow's event as already past. Re-anchor
 * its calendar date at local midnight instead, which matches how a
 * non-recurring all-day event's `startDate` is built.
 *
 * Note this is deliberately not `setZone`: that keeps the instant and shifts
 * the date (rendering the previous day), which is the opposite of what's wanted.
 * A timed key is a real instant, so it only changes zone for display.
 */
export const toInstanceDisplayStart = (
  instanceStart: DateTime,
  isAllDay: boolean,
  displayTimezone: string
): DateTime =>
  isAllDay
    ? datePartInZone(instanceStart, displayTimezone)
    : instanceStart.setZone(displayTimezone);

/** Wire value for Create/UpdateEventMessageInput.instance; the server re-normalizes it. */
export const toInstanceInput = (instanceStart: DateTime): string | null =>
  instanceStart.toUTC().toISO();

/**
 * Resolves a stored `EventMessage.instance` to the instant it identifies, so it
 * can be compared against `getInstanceStart`.
 *
 * The server stores three shapes: a date-only string for recurring all-day
 * instances (normalizeInstance -> toISODate), UTC ISO, and offset ISO. All-day
 * uses the same lexical date-part strip as `createEntryDate` — parsing an
 * offset-bearing all-day string in UTC would shift the calendar day.
 */
export const toInstanceMillis = (
  instance: string,
  isAllDay: boolean
): number =>
  isAllDay
    ? DateTime.fromISO(instance.replace(/T.*$/, ''), {
        zone: TIME_ZONE_UTC,
      }).toMillis()
    : DateTime.fromISO(instance).toMillis();

/**
 * The zone a series recurs in. A timed event repeats on its authored wall clock
 * (9am in the author's zone stays 9am even when the author's zone and the
 * viewer's zone shift DST on different dates). An all-day event has no authored
 * time at all: its `timeZone` is a hard-coded UTC sentinel (see
 * createDecoratedMagicEvent) while its `startDate` is midnight in the display
 * zone, so it has to keep recurring on the display-zone calendar or every
 * instance lands on the previous day for viewers east of UTC.
 */
export const getRecurrenceZone = (
  event: Pick<DecoratedEvent, 'isAllDay' | 'startDate' | 'timeZone'>
): string | Zone => (event.isAllDay ? event.startDate.zone : event.timeZone);

/**
 * rrule works in a fake-UTC space: `dtstart` and the instances it returns
 * carry the series' wall clock stamped as UTC.
 * https://github.com/jkbrzt/rrule?tab=readme-ov-file#important-use-utc-dates
 *
 * `startDate` has already been converted to the display zone by
 * `createEntryDate`, so it must be moved back into the recurrence zone before
 * its wall clock is captured — otherwise the wall clock handed to rrule belongs
 * to one zone and gets reinterpreted in another, putting every instance (the
 * first one included) at the wrong instant.
 */
export const toRRuleAnchor = (
  startDate: DateTime,
  recurrenceZone: string | Zone
): DateTime =>
  startDate.setZone(recurrenceZone).toUTC(0, { keepLocalTime: true });

/** Reads an instance back out of that fake-UTC space. */
export const fromRRuleDate = (
  date: Date,
  recurrenceZone: string | Zone
): DateTime =>
  DateTime.fromJSDate(date)
    .toUTC()
    .setZone(recurrenceZone, { keepLocalTime: true });

/**
 * The instance a recurring event falls back to when the URL names none: the
 * first at or after `now`, else the last one — a finished series stays on its
 * final instance so the messages panel shows its past-event state rather than
 * nothing. Non-recurring events and unparsable rules resolve to the event start.
 *
 * `range` is the span the surface behind the modal is rendering, and only sizes
 * the expansion window — the instance itself is still picked relative to `now`,
 * because a message can only be scheduled forward. Passing it is what keeps
 * this in agreement with the expander when a past range is on screen: without
 * it the rule would stop short of that range, `after` would find nothing, and
 * the fallback below would land on an instance no card ever rendered.
 */
export const getNextInstanceStart = (
  event: RecurringEvent,
  now: DateTime = DateTime.utc(),
  range?: Partial<DateTimeRange> | null
): DateTime => {
  if (!event.rrule) {
    return getInstanceStart(event);
  }

  try {
    // Anchored exactly as expandRecurringEvents anchors it, so the instance
    // resolved here is one the planner cards actually render.
    const recurrenceZone = getRecurrenceZone(event);
    const options = RRule.parseString(event.rrule);
    const dtstart = toRRuleAnchor(event.startDate, recurrenceZone);
    options.dtstart = dtstart.toJSDate();

    // Both the window anchor and `now` have to enter the same fake-UTC space as
    // dtstart, or they are offset by the recurrence zone.
    const anchoredNow = toRRuleAnchor(now, recurrenceZone);

    if (!options.until && !options.count) {
      options.until = getFallbackUntil({
        dtstart,
        windowAnchor: toRRuleAnchor(
          getRecurrenceWindowAnchor(now, range),
          recurrenceZone
        ),
      }).toJSDate();
    }

    const rruleSet = new RRuleSet();
    rruleSet.rrule(new RRule(options));

    const after = rruleSet.after(anchoredNow.toJSDate(), true);
    const instanceDate = after ?? rruleSet.all().at(-1);

    return instanceDate
      ? getInstanceStart({
          isAllDay: event.isAllDay,
          startDate: fromRRuleDate(instanceDate, recurrenceZone),
        })
      : getInstanceStart(event);
  } catch {
    // An unparsable rule shouldn't strand the modal; fall back to the series start.
    return getInstanceStart(event);
  }
};
